import type { Command } from '@fabricos/protocol-ts';
import { sendCommand } from '@/hooks/useHubSocket';
import { computePhaseTargets, type SwarmDrone, type SwarmWaypoint } from './phase';
import type { Formation } from './formations';

const PHASE_GAP_MS = 4_000;
const TAKEOFF_BATCH_SIZE = 2;
const TAKEOFF_BATCH_GAP_MS = 4_000;
const WITHIN_BATCH_STAGGER_MS = 500;
const RTL_STAGGER_MS = 300;

function makeId(): string {
  return `swarm-${Math.random().toString(36).slice(2, 8)}-${Date.now()}`;
}

/** Dispatch a two-phase swarm goto. Returns a cancel function. */
export function swarmGoto(
  drones: SwarmDrone[],
  target: SwarmWaypoint,
  formation: Formation,
  spacingM = 8,
  altitudeStaggerM = 2,
): () => void {
  const targets = computePhaseTargets(drones, target, formation, spacingM, altitudeStaggerM);
  // Phase 1 — fan out concentric.
  for (const drone of drones) {
    const wp = targets.phase1[drone.robotId];
    if (!wp) continue;
    fireGoto(drone.robotId, wp);
  }
  // Phase 2 — rigid translate after PHASE_GAP_MS.
  const handle = window.setTimeout(() => {
    for (const drone of drones) {
      const wp = targets.phase2[drone.robotId];
      if (!wp) continue;
      fireGoto(drone.robotId, wp);
    }
  }, PHASE_GAP_MS);
  return () => window.clearTimeout(handle);
}

export function swarmTakeoff(robotIds: string[], altM: number) {
  // Batched: BATCH_SIZE drones launch together, BATCH_GAP between batches,
  // WITHIN_BATCH_STAGGER between drones in the same batch.
  for (let i = 0; i < robotIds.length; i++) {
    const batch = Math.floor(i / TAKEOFF_BATCH_SIZE);
    const within = i % TAKEOFF_BATCH_SIZE;
    const delay = batch * TAKEOFF_BATCH_GAP_MS + within * WITHIN_BATCH_STAGGER_MS;
    const robotId = robotIds[i]!;
    window.setTimeout(() => {
      const altPerSlot = altM + i * 2; // altitude stagger
      sendCommand({
        kind: 'arm',
        id: makeId(),
        robot_id: robotId,
        priority: 'normal',
      });
      window.setTimeout(() => {
        sendCommand({
          kind: 'takeoff',
          id: makeId(),
          robot_id: robotId,
          priority: 'normal',
          alt_m: altPerSlot,
        });
      }, 1500);
    }, delay);
  }
}

export function swarmRtl(robotIds: string[]) {
  for (let i = 0; i < robotIds.length; i++) {
    const robotId = robotIds[i]!;
    window.setTimeout(() => {
      sendCommand({
        kind: 'rtl',
        id: makeId(),
        robot_id: robotId,
        priority: 'normal',
      });
    }, i * RTL_STAGGER_MS);
  }
}

function fireGoto(robotId: string, wp: SwarmWaypoint) {
  const cmd: Command = {
    kind: 'goto',
    id: makeId(),
    robot_id: robotId,
    priority: 'normal',
    lat: wp.lat,
    lon: wp.lon,
    alt_m: wp.alt_m,
    speed_ms: null,
    yaw_rad: null,
  };
  sendCommand(cmd);
}
